<template>
  <div>
    <div v-if="loading">
      <v-card shaped>
        <v-skeleton-loader
          ref="skeleton"
          type="table"
          class="mx-auto"
        ></v-skeleton-loader>
      </v-card>
    </div>
    <div v-else>
      <v-card shaped>
        <v-card-title>
          <v-row class="d-flex align-center">
            <v-col cols="8">
              Attendees
            </v-col>
            <v-col cols="4">
              <v-text-field
                v-model="search"
                append-icon="mdi-magnify"
                label="Search"
                single-line
                hide-details
              ></v-text-field>
            </v-col>
          </v-row>
        </v-card-title>
        <v-data-table
          v-model="checkedInAttendees"
          :headers="headers"
          :items="filteredAttendees"
          :search="search"
          :loading="computedLoadingAttendees"
          show-select
        >
          <template v-slot:top>
            <v-container>
              <v-row class="d-flex justify-space-around align-center">
                <v-col cols="3">
                  <v-switch 
                    v-model="hideCheckedInAttendees" 
                    label="Hide Checked In" 
                    class="pa-3"
                  ></v-switch>
                </v-col>
                <v-col>
                  <AddAttendees :session="session" :employees="employees" />
                </v-col>
                <v-col class="shrink d-flex justify-end">
                  <v-btn rounded text>
                    <v-icon left >mdi-cloud-download</v-icon>
                    <vue-json-to-csv 
                      :json-data="downloadData"
                    ></vue-json-to-csv>
                  </v-btn>
                </v-col>
                <v-col class="shrink d-flex justify-end">
                  <AddAttendeesGroup :session="session" :employees="employees" />
                  <CopyEmailsToClipboard class="pl-2" :emails="emailsToCopyToClipboard"/>
                </v-col>
              </v-row>
            </v-container>
          </template>
          <template v-slot:header.data-table-select>
            Checked In
          </template>
          <template 
            v-if="hideCheckInAction"
            v-slot:item.data-table-select="{ item }"
          >
            <div class="d-flex pa-0">
              <v-simple-checkbox
                disabled
                :value="checkDisabledCheckbox(item)"
              ></v-simple-checkbox>
            </div>
          </template>
          <template v-slot:item.avatar="{ item }" >
            <Avatar :employee="item" :size="30" :hideHeadline="true" />
          </template>
          <template v-slot:item.fields.Name="{ item }" >
            <router-link 
              :to="{ name: 'Employee', params: { id: item.id } }"
            >
            {{ item.fields.Name }}
            <v-chip
              x-small
              v-bind="attrs" v-on="on"
              v-if="item.fields.Status === 'Inactive'" 
              class="error text-uppercase"
            >Inactive</v-chip>
            </router-link>
          </template>
          <template v-slot:item.id="{ item }" >
            <router-link 
              :to="{ name: 'QRCode', query: { code: generateCode(item) }}"
            >
            {{ item.fields.Name }}
            </router-link>
          </template>
          <template v-slot:item.actions="{ item }">
            <v-icon
              v-if="!loadingRemoval"
              small
              @click="remove(item)"
            >
              mdi-delete
            </v-icon>
            <v-icon
              v-else
              small
            >
              mdi-delete-forever-outline
            </v-icon>
          </template>
        </v-data-table>
      </v-card>
    </div>
  </div>
</template>

<script>
  import VueJsonToCsv from 'vue-json-to-csv'

  import Avatar from '@/components/employee/Avatar'
  import AddAttendees from '@/components/session/AddAttendees'
  import AddAttendeesGroup from '@/components/session/AddAttendeesGroup'
  import CopyEmailsToClipboard from '@/components/session/CopyEmailsToClipboard'
  
  export default {
    name: 'AttendeesTable',
    components: {
      Avatar,
      AddAttendees,
      AddAttendeesGroup,
      CopyEmailsToClipboard,
      VueJsonToCsv
    },
    props: {
      loading: Boolean,
      session: Object
    },
    data () {
      return {
        search: '',
        loadingAttendees: true,
        loadingRemoval: false,
        hideCheckedInAttendees: false,
        hideCheckInAction: false,
        headers: [
          {
            text: '',
            sortable: false,
            value: 'avatar'
          },
          {
            text: 'Name',
            align: 'start',
            sortable: true,
            value: 'fields.Name'
          },
          // {
          //   text: 'Code',
          //   align: 'start',
          //   sortable: true,
          //   value: 'id'
          // },
          {
            text: 'Email',
            align: 'start',
            sortable: true,
            value: 'fields.Email'
          },
          {
            text: 'Manager Email',
            align: 'start',
            sortable: true,
            value: 'fields.Manager Email'
          },
          {
            text: 'Department',
            align: 'start',
            sortable: true,
            value: 'fields.Department'
          },
          {
            text: 'Location',
            align: 'start',
            sortable: true,
            value: 'fields.Location Formatted'
          },
          {
            text: 'People Leader',
            align: 'start',
            sortable: true,
            value: 'fields.Manager Position'
          },
          {
            text: 'Actions',
            align: 'center',
            sortable: false,
            value: 'actions'
          }
        ]
      }
    },
    computed: {
      attendees () {
        return this.$store.getters.getEmployees.filter(e => {
          if (e.fields.Sessions) {
            return e.fields.Sessions.includes(this.session.id)
          }
        })
      },
      downloadData () {
        return this.attendees.map(e => {
          let mappedEmployee = {
            name: e.fields.Name
          }
          let downloadHeaders = this.headers.filter(h => {
            return h.value.includes('fields.')
          })
          downloadHeaders.forEach(prop => {
            let field = prop.value.split('fields.')[1]
            mappedEmployee[field] = e.fields[field]
          })
          return mappedEmployee
        })
      },
      filteredAttendees () {
        if (this.hideCheckedInAttendees) {
          const checkIns = this.checkedInAttendees.map(a => {
            return a.id
          })
          return this.attendees.filter(a => {
            return !checkIns || !checkIns.includes(a.id)
          })
        } else {
          return this.attendees
        }
      },
      emailsToCopyToClipboard () {
        // use filtered attendees so that filters apply when copying emails
        return this.filteredAttendees.map(a => {
          return a.fields.Email
        })
      },
      computedLoadingAttendees () {
        return this.loadingAttendees || 
               this.$store.getters.getSessionAttendeesLoading
      },
      checkedInAttendees: {
        get () {
          if (!this.session.fields['Checked In']) {
            return []
          }
          return this.attendees.filter(a => {
            return this.session.fields['Checked In'].includes(a.id)
          })
        },
        set (newVal) {
          const that = this
          this.hideCheckInAction = true
          this.loadingAttendees = true
          const allCheckedInAttendees = newVal.map(checkedInAttendee => {
            return checkedInAttendee.id
          })
          this.$store.dispatch('updateRecords', {
            table: 'Sessions',
            records: [{
              id: this.session.id,
              fields: {
                'Checked In': allCheckedInAttendees
              }
            }]
          })
          .then(() => {
            this.$store.dispatch('getRecords', {
              table: 'Sessions',
              view: 'All Sessions',
              dispatch: 'setSessions'
            })
            .then(() => {
              that.hideCheckInAction = false
              that.loadingAttendees = false
            })
          })
        }
      },
      employees () {
        return this.$store.getters.getEmployees
      }
    },
    created () {
      this.init()
    },
    methods: {
      init () {
        return new Promise((resolve, reject) => {
          this.$store.dispatch('getRecords', {
            table: 'Employees',
            view: 'All Employees',
            dispatch: 'setEmployees'
          })
          .then(() => {
            this.loadingAttendees = false
            return resolve()
          })
          .catch((err) => {
            return reject(err)
          })
        })
      },
      remove (employee) {
        this.loadingRemoval = true
        const updatedAttendees = this.session.fields.Attendees.filter(e => {
          return e !== employee.id
        })
        const updatedCheckIns = this.session.fields['Checked In'] ? 
                                this.session.fields['Checked In'].filter(e => {
                                  return e !== employee.id
                                }) : []
        this.$store.dispatch('updateRecords', {
          table: 'Sessions',
          records: [{
            id: this.session.id,
            fields: {
              'Attendees': updatedAttendees,
              'Checked In': updatedCheckIns
            }
          }]
        })
        .then(() => {
          this.loadingAttendees = true
          this.init().then(() => this.loadingRemoval = false)
        })
      },
      checkDisabledCheckbox (employee) {
        if (employee.fields['Sessions Checked In']) {
          if (employee.fields['Sessions Checked In']
              .includes(this.session.id)) {
            return true
          } else {
            return false
          }
        } else {
          return false
        }
      },
      generateCode (employee) {
        return btoa(JSON.stringify({
          sessionId: this.session.id,
          employeeId: employee.id
        }))
      }
    }
  }
</script>
